import invariant from 'tiny-invariant'

import { API_PATH_INTERNAL } from '@/lib/constants'
import apiService from '@/lib/services/apiService'
import {
  CreateReferralLinkAsToCompanyRequest,
  CreateReferralLinkRequest,
  GetReferralLinksParams,
  UpdateReferralLinkAsToCompanyRequest,
  UpdateReferralLinkCallbackTypeRequest,
  UpdateReferralLinkCodeRequest,
  UpdateReferralLinkMeetingTypeRequest,
  UpdateReferralLinkNameRequest,
  UpdateReferralLinkRequest,
} from '@/types/requests/referral-links'
import { ValidateReferralLinkClientDetailsRequest } from '@/types/requests/referrals'
import {
  DuplicateReferralLinkResponse,
  ReferralLinkCollection,
  ReferralLinkItem,
  ReferralLinkItemService,
  ShareableReferralLinkCollection,
  UpdateReferralLinkAsToCompanyResponse,
  UpdateReferralLinkCallbackTypeResponse,
  UpdateReferralLinkMeetingTypeResponse,
} from '@/types/responses/referral-links'

class ReferralLinkService {
  private endpoint = `${API_PATH_INTERNAL}/referral-links`

  async getReferralLinks(
    params: GetReferralLinksParams,
  ): Promise<ReferralLinkCollection> {
    const response = await apiService.get(this.endpoint, {
      params,
    })

    return response.data
  }

  async getShareableLinks(): Promise<ShareableReferralLinkCollection> {
    const response = await apiService.get(`${this.endpoint}/shareable`)

    return response.data
  }

  async getReferralLinkByIdOrCode(
    referralLinkIdOrCode: string,
  ): Promise<ReferralLinkItem> {
    const response = await apiService.get(
      `${this.endpoint}/by-id-or-code/${referralLinkIdOrCode}`,
    )

    return response.data
  }

  async createReferralLink(request: CreateReferralLinkRequest) {
    const response = await apiService.post(this.endpoint, request)

    return response.data
  }

  async createReferralLinkAsToCompany(
    request: CreateReferralLinkAsToCompanyRequest,
  ) {
    const response = await apiService.post(
      `${this.endpoint}/as-to-company`,
      request,
    )

    return response.data
  }

  async updateReferralLink(
    referralLinkId: number,
    request: UpdateReferralLinkRequest,
  ): Promise<unknown> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralLinkId}`,
      request,
    )

    return response.data
  }

  async deleteReferralLink(referralLinkId: number) {
    const response = await apiService.post(
      `${this.endpoint}/${referralLinkId}/delete`,
      {},
    )

    return response.data
  }

  async validateClientDetails(
    referralLinkCode: string,
    request: ValidateReferralLinkClientDetailsRequest,
  ): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/${referralLinkCode}/validate-client-details`,
      request,
    )

    return response.data
  }

  async duplicateLink(
    referralLinkId: number,
  ): Promise<DuplicateReferralLinkResponse> {
    const response = await apiService.post(
      `${this.endpoint}/${referralLinkId}/duplicate`,
      {},
    )

    return response.data
  }

  async updateReferralLinkCode(
    referralLinkId: number,
    request: UpdateReferralLinkCodeRequest,
  ) {
    const response = await apiService.post(
      `${this.endpoint}/${referralLinkId}/update-code`,
      request,
    )

    return response.data
  }

  async updateName(
    referralLinkId: number,
    request: UpdateReferralLinkNameRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${referralLinkId}/name`,
      request,
    )

    return response.data
  }

  async updateMeetingType(
    referralLinkId: number,
    request: UpdateReferralLinkMeetingTypeRequest,
  ): Promise<UpdateReferralLinkMeetingTypeResponse> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralLinkId}/meeting-type`,
      request,
    )

    return response.data
  }

  async updateCallbackType(
    referralLinkId: number,
    request: UpdateReferralLinkCallbackTypeRequest,
  ): Promise<UpdateReferralLinkCallbackTypeResponse> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralLinkId}/callback-type`,
      request,
    )

    return response.data
  }

  async updateAsToCompany(
    referralLinkId: number,
    request: UpdateReferralLinkAsToCompanyRequest,
  ): Promise<UpdateReferralLinkAsToCompanyResponse> {
    const response = await apiService.patch(
      `${this.endpoint}/${referralLinkId}/as-to-company`,
      request,
    )

    return response.data
  }

  async toggleIsCompassLink(referralLinkId: number) {
    const response = await apiService.post(
      `${this.endpoint}/${referralLinkId}/toggle-is-compass-link`,
      {},
    )

    return response.data
  }

  async setDefaultForRelationship(referralLinkId: number) {
    const response = await apiService.post(
      `${this.endpoint}/${referralLinkId}/set-default-for-relationship`,
      {},
    )

    return response.data
  }

  hasSelectedRestrictedService(
    referralLink: ReferralLinkItem,
    selectedServiceIris: IRI[],
  ): boolean {
    return this.getServicesFromServiceIris(
      referralLink,
      selectedServiceIris,
    ).some((service) => service.isRestricted)
  }

  getServicesFromServiceIris(
    referralLink: ReferralLinkItem,
    selectedServiceIris: IRI[],
  ): ReferralLinkItemService[] {
    return selectedServiceIris.map((serviceIri) => {
      const service = referralLink.toCompany.services.find(
        (service) => service['@id'] === serviceIri,
      )

      invariant(service, `Failed to find service by IRI: ${serviceIri}`)

      return service
    })
  }

  getServiceAreasFromServices(
    referralLink: ReferralLinkItem,
    selectedServiceIris: IRI[],
  ): ReferralLinkItemService['serviceArea'][] {
    return selectedServiceIris.map((serviceIri) => {
      const service = referralLink.toCompany.services.find(
        (service) => service['@id'] === serviceIri,
      )

      invariant(service, `Failed to find service by IRI: ${serviceIri}`)

      return service.serviceArea
    })
  }

  async updateFromCompanyUsersBcc(
    referralLinkId: number,
    fromCompanyUserIdsToBcc: number[],
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${referralLinkId}/from-company-users-to-bcc`,
      {
        fromCompanyUserIdsToBcc,
      },
    )

    return response.data
  }
}

const referralLinkService = new ReferralLinkService()

export default referralLinkService
